import { AccordionSectionPresentationProps } from "@DSG/MobiusOverrides/AccordionSection/AccordionSection";
import colorPallete, { bodyFonts, buttonFonts, headlineFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetGroups from "@insite/client-framework/Types/WidgetGroups";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { HiddenProps } from "@insite/mobius/Hidden";
import Img from "@insite/mobius/Img/Img";
import { Hidden } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    dsgAccordionData = "dsgAccordionData",
    accordionByDefaultActive = "accordionByDefaultActive",
    additionalContentOnBottom = "additionalContentOnBottom",
}

interface DsgAccordionItem {
    fields: {
        title: string;
        textData: string;
        image: string;
        showImage: boolean;
        imagePosition: string;
        showSameImageForMobile: boolean;
        imageMobile: string;
        imageAltText: string;
        additionalContentOnBottom: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.dsgAccordionData]: DsgAccordionItem[];
        [fields.accordionByDefaultActive]: string;
        [fields.additionalContentOnBottom]: string;
    };
}

export interface DsgAccordionStyles {
    dsgAccordionWrapper?: InjectableCss;
    dsgAccordionInnerWrapper?: InjectableCss;
    dsgAccordionContent?: InjectableCss;
    dsgAccordionMainTitle?: TypographyPresentationProps;
    dsgAccordionSectionWrapper?: InjectableCss;
    dsgAccordionSection?: AccordionSectionPresentationProps;
    imageWrapper?: InjectableCss;
    imageDesktop?: HiddenProps;
    imageMobile?: HiddenProps;
    additionalContentOnBottom?: InjectableCss;
}

export const dsgAccordionStyles: DsgAccordionStyles = {
    dsgAccordionWrapper: {
        css: css`
            max-width: 1280px;
            margin: 24px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            margin: 48px 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    dsgAccordionContent: {
        css: css`
            p {
                font-size: ${bodyFonts.body2RegularLg.fontSize};
                line-height: ${bodyFonts.body2RegularLg.lineHeight};
                color: ${colorPallete.grey800};
                margin-bottom: 0;
                &:last-child {
                    margin-bottom: 0;
                }
            }
            strong {
                font-weight: ${themeFontWeights.semiBold};
            }
            ul,
            ol {
                margin: 0 0 0 24px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            p {
                                font-size: ${bodyFonts.body2RegularSm.fontSize};
                                line-height: ${bodyFonts.body2RegularSm.lineHeight};
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    dsgAccordionMainTitle: {
        weight: headlineFonts.headline3SemiBoldLg.fontWeight,
        css: css`
            margin-bottom: 24px;
            color: ${colorPallete.grey800};
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-bottom: 48px;
                    `,
                    null,
                ],
                "min",
            )}
        `,
    },
    dsgAccordionSectionWrapper: {
        css: css`
            border-bottom: 1px solid ${colorPallete.grey500};
        `,
    },
    dsgAccordionSection: {
        mergeCss: true,
        headerProps: {
            css: css`
                button {
                    border: none;
                    background: ${colorPallete.white};
                    padding: 24px;
                    span {
                        color: ${colorPallete.grey800};
                    }
                    [class*="TransitionManager"] {
                        bottom: 0;
                        top: 0;
                    }
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    padding: 24px 0;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                button {
                                    [class*="IconWrapper"] {
                                        svg {
                                            display: none;
                                        }
                                        &:before {
                                            content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M3.87156 6.49016L2.10156 8.27016L12.0016 18.1602L21.9016 8.26016L20.1316 6.49016L12.0016 14.6202L3.87156 6.49016Z' fill='%230C0C0C'/%3E%3C/svg%3E");
                                            height: 24px;
                                            width: 24px;
                                        }
                                    }
                                    &[aria-expanded="true"] {
                                        [class*="IconWrapper"] {
                                            &:before {
                                                content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M20.1284 17.5098L21.8984 15.7298L11.9984 5.83984L2.09844 15.7398L3.86844 17.5098L11.9984 9.37984L20.1284 17.5098Z' fill='%230C0C0C'/%3E%3C/svg%3E");
                                            }
                                        }
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        panelProps: {
            css: css`
                padding: 0 0 24px;
                border: none;
                > div {
                    display: flex;
                    gap: 24px;
                    flex-direction: column;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                padding: 0 24px 24px;
                                > div {
                                    flex-direction: row;
                                    align-items: flex-start;
                                    &.imageOnRight {
                                        flex-direction: row-reverse;
                                        justify-content: space-between;
                                    }
                                }
                                .withImageWrapper {
                                    margin-top: -8px;
                                }
                            `,
                        ],
                        "min",
                    )}
            `,
        },
        titleTypographyProps: {
            size: bodyFonts.body1SemiBoldLg.fontSize,
            lineHeight: bodyFonts.body1SemiBoldLg.lineHeight,
            weight: bodyFonts.body1SemiBoldLg.fontWeight,
            css: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                font-size: ${bodyFonts.body2SemiBoldSm.fontSize};
                                line-height: ${bodyFonts.body2SemiBoldSm.lineHeight};
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    },
    imageWrapper: {
        css: css`
            display: flex;
            > div {
                width: 100%;
            }
            img {
                width: 100%;
                height: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        min-width: 207px;
                        img {
                            max-width: 207px;
                        }
                    `,
                ],
                "min",
            )}
        `
    },
    imageDesktop: {
        below: "md",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        display: flex;
                    `,
                ],
                "min",
            )}
        `
    },
    imageMobile: {
        above: "sm",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        display: flex;
                    `,
                ],
                "max",
            )}
        `
    },
    additionalContentOnBottom: {
        css: css`
            margin-top: 24px;
            [class*="StyledButton"],
            [class*="StyledClickable"] {
                & {
                    min-height: 38px;
                    padding: 5px 24px;
                    border-radius: 30px;
                    font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize} /
                        ${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                    text-align: center;
                    justify-content: center;
                }
                
                > span {
                    display: flex;
                    column-gap: 8px;
                    align-items: center;
                    [class*="LazyImageStyle"] {
                        padding: 0 !important;
                        display: flex !important;
                    }
                }
               
                &,
                &:hover,
                &:focus,
                &:active {
                    span {
                        color: inherit;
                    }
                }
                span {
                    display: flex;
                    align-items: center;
                    &[class*="LazyImageStyle"] {
                        padding: 0 !important;
                        display: flex !important;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            & {
                                min-height: 48px;
                                padding: 4px 32px;
                            }
                        `,
                    ],
                    "min",
                )}
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin-top: 48px;
                    `,
                ],
                "min",
            )}
        `
    }
};

const styles = dsgAccordionStyles;

export const DsgAccordion: FC<OwnProps> = ({ fields }) => {
    const { dsgAccordionData } = fields;

    const defaultAccordian = (index: Number) => {
        let status = false;
        if (fields.accordionByDefaultActive === "allOpened") {
            status = true;
        } else if (fields.accordionByDefaultActive === "allClosed") {
            status = false;
        } else if (fields.accordionByDefaultActive === "firstActive" && index === 0) {
            status = true;
        } else {
            status = false;
        }
        return status;
    };

    return (
        <StyledWrapper {...styles.dsgAccordionWrapper}>
            <StyledWrapper className="list-wrapper" {...styles.dsgAccordionInnerWrapper}>
                <StyledWrapper className="title-wrapper">
                    {fields.title && (
                        <ContentWrapper alignment={fields.titlePosition}>
                            <Typography {...styles.dsgAccordionMainTitle} as="h2" variant="h2">
                                {fields.title}
                            </Typography>
                        </ContentWrapper>
                    )}
                </StyledWrapper>
                {dsgAccordionData.length > 0 && (
                    <Accordion headingLevel={2}>
                        {dsgAccordionData.map((item, index) => (
                            <>
                                {item.fields.title && (
                                    <StyledWrapper key={index} {...styles.dsgAccordionSectionWrapper}>
                                        <ManagedAccordionSection
                                            initialExpanded={defaultAccordian(index)}
                                            {...styles.dsgAccordionSection}
                                            title={item.fields.title}
                                        >
                                            <StyledWrapper  className={`
                                                    ${item.fields.imagePosition === "right" ? 'imageOnRight' : ""}
                                                    ${item.fields.image ? 'withImageWrapper' : 'withoutImageWrapper'}
                                                `}>  
                                                {item.fields.showImage && (
                                                    <> 
                                                        {(item.fields.image && item.fields.showSameImageForMobile) && (
                                                            <StyledWrapper {...styles.imageWrapper}>
                                                                <Img src={item.fields.image} altText={item.fields.imageAltText || ''} />  
                                                            </StyledWrapper>
                                                        )}
                                                        {(item.fields.image && !item.fields.showSameImageForMobile) && (
                                                            <StyledWrapper {...styles.imageWrapper}>
                                                                {item.fields.image && (
                                                                    <Hidden {...styles.imageDesktop}>
                                                                        <Img src={item.fields.image} altText={item.fields.imageAltText || ''} />
                                                                    </Hidden>
                                                                )}
                                                                {item.fields.imageMobile && (
                                                                    <Hidden {...styles.imageMobile}>
                                                                        <Img src={item.fields.imageMobile} altText={item.fields.imageAltText || ''} />
                                                                    </Hidden>
                                                                )}
                                                            </StyledWrapper>
                                                        )}
                                                   </>
                                                )}     
                                                {item.fields.textData && (
                                                    <StyledWrapper {...styles.dsgAccordionContent}>
                                                        {parse(item.fields.textData, parserOptions)}
                                                    </StyledWrapper>
                                                )}
                                            </StyledWrapper>
                                        </ManagedAccordionSection>
                                    </StyledWrapper>
                                )}
                            </>
                        ))}
                    </Accordion>
                )}
                {fields.additionalContentOnBottom && (
                    <StyledWrapper {...styles.additionalContentOnBottom}>
                        {parse(fields.additionalContentOnBottom, parserOptions)}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: DsgAccordion,
    definition: {
        group: "Basic",
        displayName: "Dsg Accordion",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.titlePosition,
                displayName: "Heading Title Position",
                editorTemplate: "RadioButtonsField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
            },
            {
                name: fields.accordionByDefaultActive,
                displayName: "Accordion Active Default",
                editorTemplate: "RadioButtonsField",
                defaultValue: "allClosed",
                fieldType: "General",
                options: [
                    {
                        displayName: "All Opened",
                        value: "allOpened",
                    },
                    {
                        displayName: "All Closed",
                        value: "allClosed",
                    },
                    {
                        displayName: "First Active",
                        value: "firstActive",
                    },
                ],
            },
            {
                name: fields.dsgAccordionData,
                editorTemplate: "ListField",
                displayName: "Accordion Data",
                getDisplay: (item: HasFields) => {
                    const { title } = item.fields;
                    if (title) {
                        return title;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "textData",
                        editorTemplate: "RichTextField",
                        displayName: "Text Content",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {
                                buttons: [
                                    "insertLink",
                                    "insertImage",
                                    "insertHR",
                                    "specialCharacters",
                                    "insertVideo",
                                    "insertFile",
                                ],
                                buttonsVisible: 3,
                            },
                            code: {
                                buttons: ["selectAll", "html", "help"],
                                buttonsVisible: 3,
                            },
                        },
                    },
                    {
                        name: "showImage",
                        displayName: "Show Image in accordion",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isVisible: widget => widget.fields.showImage,
                    },
                    {
                        name: "imagePosition",
                        displayName: "Image Position",
                        editorTemplate: "RadioButtonsField",
                        defaultValue: "left",
                        options: [
                            {
                                displayName: "Left",
                                value: "left",
                            },
                            {
                                displayName: "Right",
                                value: "right",
                            },
                        ],
                        isVisible: widget => widget.fields.showImage,
                    },
                    {
                        name: "showSameImageForMobile",
                        displayName: "Show Same Image for Mobile",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                        isVisible: widget => widget.fields.showImage,
                    },
                    {
                        name: "imageMobile",
                        displayName: "Image For Mobile",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: widget => widget.fields.showImage && !widget.fields.showSameImageForMobile,
                    },
                    {
                        name: "imageAltText",
                        displayName: "Image Alt Text",
                        editorTemplate: "TextField",
                        defaultValue: '',
                        isVisible: widget => widget.fields.showImage,
                    }
                ],
            },
            {
                name: fields.additionalContentOnBottom,
                editorTemplate: "RichTextField",
                displayName: "Additional Content On Bottom",
                defaultValue: "",
                extendedConfig: { height: 170 },
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: block;
    border-bottom: 1px solid ${colorPallete.grey500};
    text-align: ${props => props.alignment ?? "left"};
    padding: ${props => props.alignment === 'center' ?  "0 50px" : '0'};
`;